import { create } from "zustand";
import {
  createSavedConnection as apiCreateSavedConnection,
  deleteSavedConnection as apiDeleteSavedConnection,
  getConnectionHealth as apiGetConnectionHealth,
  isPostgresLike,
  listSavedConnections as apiListSavedConnections,
  updateSavedConnection as apiUpdateSavedConnection,
  type ConnectionType,
  type CreateSavedConnectionRequest,
  type SavedConnection,
  type SavedConnectionScope,
  type UpdateSavedConnectionRequest,
} from "components/canvas/leftSide/import/Postgres/savedConnectionsApi";
import { createLogger } from "debug/DebugingLogger";

const log = createLogger("connectionsStore");

const FRESHNESS_WINDOW_MS = 30_000;

/** Each probe opens a real connection to the user's external DB, so
 *  results stay valid much longer than the list itself. */
const HEALTH_FRESHNESS_WINDOW_MS = 5 * 60_000;

export interface ConnectionHealthEntry {
  state: "checking" | "reachable" | "unreachable";
  reason?: string;
  /** Client clock, unix ms. Drives both the staleness gate and the
   *  "checked Nm ago" tooltip text. */
  checkedAt?: number;
}

/** Identity of a scope, for staleness. One cache, not one per org: a scope
 *  change is treated as staleness and refetches, which keeps the store's shape
 *  (and its 17 consumers) unchanged while making a cross-org list impossible to
 *  serve from cache — the 30s window used to survive an org switch. */
const scopeKey = (scope: SavedConnectionScope): string =>
  scope.modelId ? `model:${scope.modelId}` : `org:${scope.orgId ?? ""}`;

interface ConnectionsState {
  /** `null` distinguishes "never loaded" from "empty". */
  list: SavedConnection[] | null;
  lastFetchedAt: number | null;
  /** The scope `list` was fetched under; a different one is stale by definition.
   *  `refetch()` with no argument reuses it — "refresh what I am looking at" —
   *  so the create/OAuth callers that just want fresh data cannot silently widen
   *  a narrowed list back to every org. */
  scope: SavedConnectionScope;
  inFlight: boolean;
  loadError: string | null;
  /** Keyed by connection id. Absent entry = never checked / not checkable. */
  health: Record<string, ConnectionHealthEntry>;
}

interface ConnectionsActions {
  fetchIfStale: (scope?: SavedConnectionScope) => Promise<void>;
  refetch: (scope?: SavedConnectionScope) => Promise<void>;
  create: (req: CreateSavedConnectionRequest) => Promise<SavedConnection>;
  update: (
    id: string,
    patch: UpdateSavedConnectionRequest,
  ) => Promise<SavedConnection>;
  remove: (id: string) => Promise<void>;
  /** Probes Postgres/Supabase connections whose last result is older
   *  than the health window. In-memory only — a reload re-probes. */
  checkHealthIfStale: (connections: SavedConnection[]) => Promise<void>;
}

type ConnectionsStore = ConnectionsState & ConnectionsActions;

export const useConnectionsStore = create<ConnectionsStore>((set, get) => ({
  list: null,
  lastFetchedAt: null,
  scope: {},
  inFlight: false,
  loadError: null,
  health: {},

  fetchIfStale: async (scope = {}) => {
    const state = get();
    if (state.inFlight) return;
    const now = Date.now();
    const isFresh =
      state.list !== null &&
      state.lastFetchedAt !== null &&
      scopeKey(state.scope) === scopeKey(scope) &&
      now - state.lastFetchedAt < FRESHNESS_WINDOW_MS;
    if (isFresh) return;
    await get().refetch(scope);
  },

  refetch: async (scope) => {
    if (get().inFlight) return;
    const effective = scope ?? get().scope;
    set({ inFlight: true });
    try {
      const list = await apiListSavedConnections(effective);
      set({
        list,
        lastFetchedAt: Date.now(),
        scope: effective,
        loadError: null,
        inFlight: false,
      });
    } catch (e) {
      log.error("refetch failed: %o", e);
      const message =
        e instanceof Error ? e.message : "Failed to load connections";
      // Keep last-known-good list visible; only surface the error.
      set({ inFlight: false, loadError: message });
    }
  },

  create: async (req) => {
    const created = await apiCreateSavedConnection(req);
    // Insert at top — matches server MRU sort for newly-created rows.
    const list = get().list;
    set({
      list: list === null ? [created] : [created, ...list],
      lastFetchedAt: Date.now(),
    });
    return created;
  },

  update: async (id, patch) => {
    const updated = await apiUpdateSavedConnection(id, patch);
    const list = get().list;
    if (list !== null) {
      set({
        list: list.map((c) => (c.id === id ? updated : c)),
        lastFetchedAt: Date.now(),
      });
    }
    // Credentials may have changed — drop the stale verdict so the next
    // checkHealthIfStale probes live.
    dropHealthEntry(set, id);
    return updated;
  },

  remove: async (id) => {
    await apiDeleteSavedConnection(id);
    const list = get().list;
    if (list !== null) {
      set({
        list: list.filter((c) => c.id !== id),
        lastFetchedAt: Date.now(),
      });
    }
    dropHealthEntry(set, id);
  },

  checkHealthIfStale: async (connections) => {
    const now = Date.now();
    const { health } = get();
    const targets = connections.filter((c) => {
      if (!isPostgresLike(c.credentials)) return false;
      const entry = health[c.id];
      if (entry?.state === "checking") return false;
      return (
        entry?.checkedAt === undefined ||
        now - entry.checkedAt >= HEALTH_FRESHNESS_WINDOW_MS
      );
    });
    if (targets.length === 0) return;
    set((s) => ({
      health: {
        ...s.health,
        ...Object.fromEntries(
          targets.map((c) => [c.id, { state: "checking" } as const]),
        ),
      },
    }));
    await Promise.allSettled(
      targets.map(async (c) => {
        try {
          const result = await apiGetConnectionHealth(c.id);
          if (result.status === "not_applicable") {
            dropHealthEntry(set, c.id);
            return;
          }
          set((s) => ({
            health: {
              ...s.health,
              [c.id]: {
                state: result.status,
                reason:
                  result.status === "unreachable" ? result.reason : undefined,
                checkedAt: Date.now(),
              },
            },
          }));
        } catch (e) {
          log.error("health check failed for %s: %o", c.id, e);
          // A transport failure says nothing about the external DB —
          // drop the entry (no dot) rather than show a false verdict.
          dropHealthEntry(set, c.id);
        }
      }),
    );
  },
}));

function dropHealthEntry(
  set: (fn: (s: ConnectionsState) => Partial<ConnectionsState>) => void,
  id: string,
) {
  set((s) => {
    if (!(id in s.health)) return {};
    const health = { ...s.health };
    delete health[id];
    return { health };
  });
}

// Selectors — called on a list snapshot, not via `useConnectionsStore(selector)`.

export const selectById = (
  list: SavedConnection[] | null,
  id: string,
): SavedConnection | undefined => list?.find((c) => c.id === id);

export const selectByType = (
  list: SavedConnection[] | null,
  type: ConnectionType,
): SavedConnection[] => (list ?? []).filter((c) => c.credentials.type === type);

export const selectByHostFragment = (
  list: SavedConnection[] | null,
  fragment: string,
): SavedConnection[] => {
  if (!fragment) return list ?? [];
  const needle = fragment.toLowerCase();
  return (list ?? []).filter((c) => {
    if (c.display_name.toLowerCase().includes(needle)) return true;
    const cr = c.credentials;
    if (cr.type === "google_oauth") {
      return cr.account_email.toLowerCase().includes(needle);
    }
    if (cr.type === "bucket") {
      if (cr.provider.toLowerCase().includes(needle)) return true;
      if (cr.scope.kind === "bucket") {
        return cr.scope.name.toLowerCase().includes(needle);
      }
      return false;
    }
    if (cr.type === "local_sqlite") {
      // Local SQLite has no host/database/username — display_name is
      // already matched above, hash search isn't useful for humans.
      return false;
    }
    return (
      cr.host.toLowerCase().includes(needle) ||
      cr.database.toLowerCase().includes(needle) ||
      cr.username.toLowerCase().includes(needle)
    );
  });
};

export const selectUnusedFor30Days = (
  list: SavedConnection[] | null,
): SavedConnection[] => {
  const cutoff = Date.now() / 1000 - 30 * 24 * 60 * 60;
  return (list ?? []).filter((c) => {
    const ts = c.last_used_at ?? c.created_at;
    return ts < cutoff;
  });
};
